import React, { useContext, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Moment from "moment";

import { SalesContext } from "../../context/salesContext";
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from "../../constants/config";
import { SALE_STATUSES, getStatusChipTone, getStatusTone } from "../../constants/saleStatus";
import { Badge, DataTable } from "../ui";

const ALL_STATUSES = "all";
const ALL_BALANCES = "all";
const BALANCE_OVERDUE = "overdue";
const BALANCE_CLEARED = "cleared";

const formatCurrency = (value) => {
    if (value == null || value === "") return null;
    const num = Number(value);
    if (Number.isNaN(num)) return null;
    return `$${num.toFixed(2)}`;
};

const computeBalance = (sale) => {
    const paid = (sale.payments || []).reduce(
        (acc, payment) => acc + Number(payment.amount || 0),
        0,
    );
    const price = Number(sale.salesPrice || 0);
    return Number((price - paid).toFixed(2));
};

const sumBy = (rows, fn) => rows.reduce((acc, row) => acc + Number(fn(row) || 0), 0);

const CustomerSalesTable = ({ customerSales = [], openSaleModal }) => {
    const { action } = useContext(SalesContext);
    const [statusFilter, setStatusFilter] = useState(ALL_STATUSES);
    const [balanceFilter, setBalanceFilter] = useState(ALL_BALANCES);

    const enrichedRows = useMemo(
        () =>
            customerSales.map((sale) => ({
                ...sale,
                balance: computeBalance(sale),
            })),
        [customerSales],
    );

    const filteredRows = useMemo(() => {
        return enrichedRows.filter((row) => {
            if (
                statusFilter !== ALL_STATUSES &&
                (row.workOrder && row.workOrder.status) !== statusFilter
            ) {
                return false;
            }
            if (balanceFilter === BALANCE_OVERDUE && row.balance <= 0) return false;
            if (balanceFilter === BALANCE_CLEARED && row.balance > 0) return false;
            return true;
        });
    }, [enrichedRows, statusFilter, balanceFilter]);

    const statusFilterConfig = useMemo(
        () => ({
            id: "status",
            label: "Filter by status",
            value: statusFilter,
            onChange: setStatusFilter,
            options: [
                { value: ALL_STATUSES, label: "All", tone: "brand" },
                ...SALE_STATUSES.map((status) => ({
                    value: status,
                    label: status,
                    tone: getStatusChipTone(status),
                })),
            ],
        }),
        [statusFilter],
    );

    const balanceFilterConfig = useMemo(
        () => ({
            id: "balance",
            label: "Filter by balance",
            value: balanceFilter,
            onChange: setBalanceFilter,
            options: [
                { value: ALL_BALANCES, label: "All", tone: "brand" },
                { value: BALANCE_OVERDUE, label: "Overdue", tone: "red" },
                { value: BALANCE_CLEARED, label: "Cleared", tone: "green" },
            ],
        }),
        [balanceFilter],
    );

    const columns = useMemo(
        () => [
            {
                id: "orderDate",
                label: "Ordered",
                sortable: true,
                cell: (row) => (row.orderDate ? Moment(row.orderDate).format("MMM D, YYYY") : null),
            },
            {
                id: "po",
                label: "PO",
                sortable: true,
                cellClassName: "font-medium",
            },
            {
                id: "product",
                label: "Product",
                sortable: true,
                hideBelow: "md",
            },
            {
                id: "cost",
                label: "Cost",
                sortable: true,
                align: "right",
                hideBelow: "lg",
                cellClassName: "tabular-nums",
                cell: (row) => formatCurrency(row.cost),
            },
            {
                id: "labourCost",
                label: "Labour",
                sortable: true,
                align: "right",
                hideBelow: "lg",
                cellClassName: "tabular-nums",
                cell: (row) => formatCurrency(row.labourCost),
            },
            {
                id: "salesPrice",
                label: "Price",
                sortable: true,
                align: "right",
                hideBelow: "sm",
                cellClassName: "tabular-nums",
                cell: (row) => formatCurrency(row.salesPrice),
            },
            {
                id: "balance",
                label: "Balance",
                sortable: true,
                align: "right",
                cellClassName: "tabular-nums",
                cell: (row) =>
                    row.balance > 0 ? (
                        <span className="font-semibold text-danger-600">
                            ${row.balance.toFixed(2)}
                        </span>
                    ) : (
                        <span className="text-ink-400">—</span>
                    ),
            },
            {
                id: "status",
                label: "Status",
                sortable: true,
                sortValue: (row) => (row.workOrder && row.workOrder.status) || "",
                cell: (row) => {
                    const status = (row.workOrder && row.workOrder.status) || "";
                    return status ? <Badge tone={getStatusTone(status)}>{status}</Badge> : null;
                },
            },
        ],
        [],
    );

    const bulkActions = useMemo(
        () => [
            {
                label: "Delete",
                variant: "danger",
                icon: <i className="fas fa-trash" aria-hidden="true" />,
                onClick: async (selectedIds) => {
                    await Promise.all(selectedIds.map((id) => action.deleteSale(id)));
                },
                successMessage: "Record(s) successfully deleted",
                errorMessage: "An error occurred while deleting. Please contact support.",
            },
        ],
        [action],
    );

    const totals = useMemo(() => {
        const cost = sumBy(filteredRows, (r) => r.cost);
        const labour = sumBy(filteredRows, (r) => r.labourCost);
        const revenue = sumBy(filteredRows, (r) => r.salesPrice);
        const balance = sumBy(filteredRows, (r) => r.balance);
        return {
            totalSales: filteredRows.length,
            totalCost: cost + labour,
            totalRevenue: revenue,
            grossProfit: revenue - cost - labour,
            outstandingBalance: balance,
        };
    }, [filteredRows]);

    return (
        <div className="space-y-3">
            {filteredRows.length > 0 && <SummaryStrip totals={totals} />}

            <DataTable
                rows={filteredRows}
                columns={columns}
                selectable
                paginated
                defaultRowsPerPage={DEFAULT_PAGE_SIZE}
                rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                defaultSort={{ id: "orderDate", direction: "desc" }}
                onRowClick={(row) => openSaleModal(row._id)}
                bulkActions={bulkActions}
                filters={enrichedRows.length > 0 ? [balanceFilterConfig, statusFilterConfig] : []}
                emptyMessage={
                    statusFilter === ALL_STATUSES && balanceFilter === ALL_BALANCES
                        ? "No purchases yet."
                        : "No purchases match the selected filters."
                }
            />
        </div>
    );
};

const SummaryStrip = ({ totals }) => (
    <div className="grid grid-cols-2 gap-3 rounded-xl border border-ink-100 bg-white p-4 sm:grid-cols-4">
        <SummaryItem label="Total sales" value={totals.totalSales} format="count" />
        <SummaryItem label="Total revenue" value={totals.totalRevenue} />
        <SummaryItem
            label="Gross profit"
            value={totals.grossProfit}
            tone={totals.grossProfit >= 0 ? "success" : "danger"}
        />
        <SummaryItem
            label="Outstanding"
            value={totals.outstandingBalance}
            tone={totals.outstandingBalance > 0 ? "danger" : "neutral"}
        />
    </div>
);

const SummaryItem = ({ label, value, tone = "neutral", format = "currency" }) => {
    const valueClass =
        tone === "danger"
            ? "text-danger-600"
            : tone === "success"
              ? "text-success-700"
              : "text-ink-900";
    const display =
        format === "count"
            ? Number(value || 0).toLocaleString()
            : `$${Number(value || 0).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
              })}`;
    return (
        <div>
            <p className="text-xs font-medium uppercase tracking-wide text-ink-400">{label}</p>
            <p className={`mt-1 text-lg font-semibold tabular-nums ${valueClass}`}>{display}</p>
        </div>
    );
};

CustomerSalesTable.propTypes = {
    customerSales: PropTypes.array,
    openSaleModal: PropTypes.func.isRequired,
};

export default CustomerSalesTable;
